// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Rafael Pereira Satyro

export type QuoteType = 'INVESTMENT' | 'PROTECTION' | 'PENSION';

export type QuoteStatus =
  | 'PENDING'
  | 'OPTIONS_READY'
  | 'SELECTED'
  | 'COMPLETED'
  | 'EXPIRED'
  | 'VOIDED';

export type QuoteOptionsStatus = 'PENDING' | 'PARTIAL' | 'COMPLETED';

/**
 * Pension product types — kept in sync with `policyMetadata.ts` so the same
 * dropdown values are reused between Policy and Quote flows.
 */
export type PensionType =
  | 'PRB_BOB'
  | 'PRSA_STANDARD'
  | 'PRSA_NON_STANDARD'
  | 'PRSA_AVC'
  | 'PERSONAL_PENSION'
  | 'DEFINED_CONTRIBUTION'
  | 'EXECUTIVE_PENSION'
  | 'MASTERTRUST'
  | 'DIRECTORS_PENSION'
  | 'ARF'
  | 'VESTED_PRSA';

/**
 * Investment product types — kept in sync with `policyMetadata.ts` so the same
 * dropdown values are reused between Policy and Quote flows.
 */
export type InvestmentType =
  | 'INVESTMENT_BOND'
  | 'REGULAR_SAVER'
  | 'CHILDS_SAVER'
  | 'TRADING_ACCOUNTS'
  | 'INVESTMENT_PORTFOLIO';

export type ProtectionType =
  | 'MORTGAGE_PROTECTION'
  | 'TERM_LIFE_INSURANCE'
  | 'WHOLE_OF_LIFE_INSURANCE'
  | 'SERIOUS_ILLNESS_COVER'
  | 'INCOME_PROTECTION';

export type CoverType = 'SINGLE' | 'JOINT' | 'DUAL';

/** Display labels for {@link PensionType}. */
export const PENSION_TYPE_LABELS: Record<PensionType, string> = {
  PRB_BOB: 'PRB / Buy-out Bond',
  PRSA_STANDARD: 'PRSA (Standard)',
  PRSA_NON_STANDARD: 'PRSA (Non-Standard)',
  PRSA_AVC: 'PRSA AVC',
  PERSONAL_PENSION: 'Personal Pension',
  DEFINED_CONTRIBUTION: 'Defined Contribution',
  EXECUTIVE_PENSION: 'Executive Pension',
  MASTERTRUST: 'Mastertrust',
  DIRECTORS_PENSION: "Director's Pension",
  ARF: 'ARF (Approved Retirement Fund)',
  VESTED_PRSA: 'Vested PRSA',
};

/** Display labels for {@link InvestmentType}. */
export const INVESTMENT_TYPE_LABELS: Record<InvestmentType, string> = {
  INVESTMENT_BOND: 'Investment Bond',
  REGULAR_SAVER: 'Regular Saver',
  CHILDS_SAVER: "Child's Saver",
  TRADING_ACCOUNTS: 'Trading Accounts',
  INVESTMENT_PORTFOLIO: 'Investment Portfolio',
};

/** Display labels for {@link ProtectionType}. */
export const PROTECTION_TYPE_LABELS: Record<ProtectionType, string> = {
  MORTGAGE_PROTECTION: 'Mortgage Protection',
  TERM_LIFE_INSURANCE: 'Term Life Insurance',
  WHOLE_OF_LIFE_INSURANCE: 'Whole of Life Insurance',
  SERIOUS_ILLNESS_COVER: 'Serious Illness Cover',
  INCOME_PROTECTION: 'Income Protection',
};

/** Display labels for {@link CoverType}. */
export const COVER_TYPE_LABELS: Record<CoverType, string> = {
  SINGLE: 'Single cover',
  JOINT: 'Joint cover',
  DUAL: 'Dual cover',
};

export interface QuoteProvider {
  id: string;
  name: string;
  logoUrl?: string;
}

export interface Quote {
  id: string;
  brokerageId: string;
  profileId: string;
  quoteType: QuoteType;
  quoteNumber: string;
  clientNames: string;
  amount: number;
  currencyCode: string;
  sentToProviders: QuoteProvider[];
  updatedAt: string;
  createdAt: string;
  createdBy: string;
  status?: QuoteStatus;
  // Product Catalogue code (catalogue-driven type — one of policy_types.code in
  // the quote's category), not the legacy narrow enum. See useProductCatalogue.
  pensionType?: string;
  investmentType?: string;
  protectionType?: string;
  coverType?: CoverType;
  termOfCover?: number;
  clientDob?: string;
  clientDobSecondary?: string;
  normalRetirementAge?: number;
  governmentLevyCovered?: boolean;
  specialRequests?: string;
  selectedOptionId?: string;
}

/**
 * Payload sent to POST /api/quotes. Mirrors QuoteCreateRequestDTO on the
 * backend; the modal builds this object and the backend persists, then
 * dispatches the async option generation.
 */
export interface QuoteCreateRequest {
  profileId: string;
  quoteType: QuoteType;
  clientNames: string;
  amount: number;
  currencyCode: string;
  clientDob?: string;
  clientDobSecondary?: string;
  // Product Catalogue code (catalogue-driven type — one of policy_types.code in
  // the quote's category), not the legacy narrow enum. See useProductCatalogue.
  pensionType?: string;
  investmentType?: string;
  protectionType?: string;
  coverType?: CoverType;
  termOfCover?: number;
  normalRetirementAge?: number;
  governmentLevyCovered?: boolean;
  specialRequests?: string;
  providerIds: string[];
}

/**
 * Payload for PUT /api/quotes/:id. Mirrors `QuoteCreateRequest` minus the
 * fields that are immutable after creation (profileId, quoteType). Provider
 * list IS editable: the backend diffs `providerIds` against the current set
 * and dispatches new mock generation for additions / soft-deletes options
 * for removals.
 */
export interface QuoteUpdateRequest {
  clientNames: string;
  amount: number;
  currencyCode: string;
  clientDob?: string;
  clientDobSecondary?: string;
  // Product Catalogue code (catalogue-driven type — one of policy_types.code in
  // the quote's category), not the legacy narrow enum. See useProductCatalogue.
  pensionType?: string;
  investmentType?: string;
  protectionType?: string;
  coverType?: CoverType;
  termOfCover?: number;
  normalRetirementAge?: number;
  governmentLevyCovered?: boolean;
  specialRequests?: string;
  providerIds: string[];
}

export interface QuoteOption {
  id: string;
  quoteId: string;
  providerId: string;
  providerName: string;
  providerLogoUrl?: string;
  /** Product (policy type) this option represents. */
  productId?: string;
  /** Provenance: TERMS_PLATFORM | TERMS_BROKERAGE. */
  source?: string;
  /** True when the option came from a brokerage-scope rate card ("Your terms" badge). */
  brokerageSpecific?: boolean;
  /** True when the request's contribution matched this option's band/limits. */
  qualifies?: boolean;
  /** Band-resolved allocation for the contribution (null when unresolvable). */
  resolvedAllocationPercent?: number | null;
  /** Point-in-time snapshot of the resolved rate-card option (richer charge fields). */
  termsSnapshot?: Record<string, unknown>;
  allocationPercent: number;
  amcPercent: number;
  fundManagementCharges: number[];
  /** Empty list = NIL exit penalties. */
  exitPenalties: number[];
  clawbacks: number[];
  selected: boolean;
  pinned: boolean;
  receivedAt: string;
}

export interface QuoteOptionsResponse {
  status: QuoteOptionsStatus;
  expectedProviderCount: number;
  receivedProviderCount: number;
  options: QuoteOption[];
}

/** Acronyms kept uppercase when humanizing a product code for display. */
const PRODUCT_CODE_ACRONYMS = new Set(['PRSA', 'AVC', 'ARF', 'PRB', 'BOB', 'DB', 'DC']);

/** SNAKE_CASE product code → readable label (e.g. PERSONAL_RETIREMENT_BOND → "Personal Retirement Bond"). */
export function humanizeProductCode(code: string): string {
  return code
    .split('_')
    .filter(Boolean)
    .map((t) =>
      PRODUCT_CODE_ACRONYMS.has(t.toUpperCase())
        ? t.toUpperCase()
        : t.charAt(0).toUpperCase() + t.slice(1).toLowerCase(),
    )
    .join(' ');
}

/**
 * Resolve a quote type product code to a display label: prefer a known legacy
 * label (still used for overlapping codes), else humanize the catalogue code.
 */
export function productTypeLabel(code: string | undefined, labels: Record<string, string>, fallback: string): string {
  if (!code) return fallback;
  return labels[code] ?? humanizeProductCode(code);
}

/**
 * Display string for the Quotes-table "Quote type" column. Returns the
 * specific product (from the catalogue-driven type code) when available,
 * falling back to a generic category label otherwise.
 */
export function formatQuoteTypeLabel(quote: Quote): string {
  switch (quote.quoteType) {
    case 'INVESTMENT':
      return productTypeLabel(quote.investmentType, INVESTMENT_TYPE_LABELS as Record<string, string>, 'Investment');
    case 'PENSION':
      return productTypeLabel(quote.pensionType, PENSION_TYPE_LABELS as Record<string, string>, 'Pension');
    case 'PROTECTION':
      return productTypeLabel(quote.protectionType, PROTECTION_TYPE_LABELS as Record<string, string>, 'Protection');
    default:
      return quote.quoteType;
  }
}
